import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { campaignAPI } from '../services/api';
import { InputAdornment, TextField } from '@mui/material';

const MyCampaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCampaigns();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, page, rowsPerPage, search]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: search,
      };
      if (currentTab !== 'all') {
        params.status = currentTab;
      }
      const response = await campaignAPI.getAllCampaigns(params);

      const data = response.data?.data || response.data || {};
      const campaignsData = data.campaigns || [];
      const totalCount = data.total || campaignsData.length; // Fallback if backend doesn't return total

      setCampaigns(Array.isArray(campaignsData) ? campaignsData : []);
      setTotal(totalCount);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Failed to fetch campaigns');
      setCampaigns([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await campaignAPI.getCampaignStats();
      setStats(response.data?.data || response.data || null);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats(null);
    }
  };

  const handleDelete = async (id, campaignName) => {
    if (window.confirm(`Are you sure you want to delete campaign "${campaignName}"? This action cannot be undone.`)) {
      try {
        await campaignAPI.deleteCampaign(id);
        toast.success('Campaign deleted successfully');
        fetchCampaigns();
        fetchStats();
      } catch (error) {
        console.error('Error deleting campaign:', error);
        toast.error(error.response?.data?.message || 'Failed to delete campaign');
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'default',
      pending_approval: 'warning',
      approved: 'info',
      active: 'success',
      paused: 'warning',
      completed: 'default',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ').toUpperCase();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          My Campaigns
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/campaigns/create')}
        >
          Create Campaign
        </Button>
      </Box>

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold">
                  {stats.totalCampaigns}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Campaigns
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {stats.activeCampaigns}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Active Campaigns
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="warning.main">
                  {stats.pendingApproval}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Pending Approval
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h4" fontWeight="bold" color="info.main">
                  {stats.totalImpressions?.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Impressions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs and Search */}
      <Paper sx={{ mb: 2, p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ flex: 1 }}
        >
          <Tab label="All" value="all" />
          <Tab label="Draft" value="draft" />
          <Tab label="Pending Approval" value="pending_approval" />
          <Tab label="Approved" value="approved" />
          <Tab label="Active" value="active" />
          <Tab label="Completed" value="completed" />
          <Tab label="Rejected" value="rejected" />
        </Tabs>
        <TextField
          size="small"
          placeholder="Search by Offer No / Name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: '300px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Campaigns Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Offer Number</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Duration</strong></TableCell>
                <TableCell><strong>Slots</strong></TableCell>
                <TableCell><strong>Impressions</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Alert severity="info">
                      No campaigns found. Create your first campaign!
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((campaign) => (
                  <TableRow key={campaign._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {campaign.offerNumber || campaign.campaignId}
                      </Typography>
                    </TableCell>
                    <TableCell>{campaign.campaignName}</TableCell>
                    <TableCell>{campaign.clientName}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {campaign.startDate && campaign.endDate && 
                         new Date(campaign.startDate).getTime() !== 0 && 
                         new Date(campaign.endDate).getTime() !== 0
                          ? `${new Date(campaign.startDate).toLocaleDateString()} - ${new Date(campaign.endDate).toLocaleDateString()}`
                          : 'Not set'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={campaign.assignedSlots?.length || 0}
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {campaign.actualImpressions?.toLocaleString()} /{' '}
                        {campaign.estimatedImpressions?.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(campaign.status)}
                        color={getStatusColor(campaign.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/campaigns/${campaign._id}`)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>

                      {(campaign.status === 'draft' || campaign.status === 'rejected') && (
                        <>
                          {campaign.status === 'rejected' && (
                            <Tooltip title="Rejection Reason">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  toast.info(`Rejection Reason: ${campaign.rejectionReason || 'No reason provided'}`);
                                }}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => navigate(`/campaigns/edit/${campaign._id}`)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(campaign._id, campaign.campaignName)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Info Box */}
      <Paper sx={{ mt: 3, p: 3, backgroundColor: '#f5f5f5' }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom>
          📌 Campaign Workflow
        </Typography>
        <Typography variant="body2" paragraph>
          1. <strong>Create Campaign</strong> - Fill in campaign details and save as draft
        </Typography>
        <Typography variant="body2" paragraph>
          2. <strong>Upload Creative</strong> - Upload your video or image content
        </Typography>
        <Typography variant="body2" paragraph>
          3. <strong>Assign Slots</strong> - Select location, screens, and time slots
        </Typography>
        <Typography variant="body2" paragraph>
          4. <strong>Submit for Approval</strong> - Send to admin for review
        </Typography>
        <Typography variant="body2">
          5. <strong>Go Live</strong> - Campaign automatically activates on start date after approval
        </Typography>
      </Paper>
    </Box>
  );
};

export default MyCampaigns;